import { Header } from "./-components/header"
import { useEffect } from "react"
import { createFileRoute } from "@tanstack/react-router"

export const Route = createFileRoute("/(site)/privacy")({
  component: PrivacyPolicy,
})

function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-24 max-w-4xl">
        <article className="prose prose-slate dark:prose-invert max-w-none">
          <h1>Privacy Policy for Knowledge Facts Ladder (KFL)</h1>
          <p className="text-muted-foreground">Last updated: 03.12.2025</p>

          <p>
            This Privacy Policy describes how Knowledge Facts Ladder ("KFL",
            "we", "us", "our") collects, uses, stores, and protects personal
            data of players and visitors ("you", "users"). By creating an
            account, participating in KFL events, or using our website, you
            agree to the terms described in this Privacy Policy.
          </p>
          <p>
            KFL is operated by an individual entrepreneur registered in Ukraine.
            If you do not agree with this Policy, please do not use our
            services.
          </p>

          <h2>1. Information We Collect</h2>
          <p>
            We collect information necessary to operate the quizzing league,
            maintain fair competition, and provide a personalized experience.
          </p>

          <h3>1.1. Information You Provide to Us</h3>
          <ul>
            <li>Name or chosen display name</li>
            <li>Email address</li>
            <li>Country (optional or provided by you)</li>
            <li>Account login information</li>
            <li>
              Payment confirmation information (from PayPal, Wise, Payoneer, or
              Monobank)
            </li>
            <li>Profile preferences</li>
            <li>Messages sent to KFL support</li>
          </ul>
          <p>
            We do not collect payment card numbers; these are handled by
            third-party payment processors.
          </p>

          <h3>1.2. Information Collected Automatically</h3>
          <ul>
            <li>Device type, browser type, and operating system</li>
            <li>IP address</li>
            <li>Login timestamps and quiz participation logs</li>
            <li>Website usage statistics</li>
          </ul>
          <p>This data helps us maintain security and technical performance.</p>

          <h3>1.3. Information Collected During Gameplay</h3>
          <p>For anti-cheating and fairness purposes, we may collect:</p>
          <ul>
            <li>Screen recordings during certain events</li>
            <li>Player video (camera), especially during Pairs and Teams</li>
            <li>Gameplay actions (answers, time used, click patterns)</li>
            <li>Suspicious activity flags</li>
            <li>Audio during recorded sessions</li>
            <li>Event metadata (start time, duration, scoring)</li>
          </ul>
          <p>
            Some events may not be recorded; others are recorded randomly or
            when cheating is suspected.
          </p>

          <h3>1.4. Information from Public Leaderboards</h3>
          <p>
            When you participate in KFL, the following may be shown publicly:
          </p>
          <ul>
            <li>Name</li>
            <li>Country</li>
            <li>Score</li>
            <li>Ranking</li>
            <li>Participation statistics</li>
          </ul>
          <p>You may request a pseudonym instead of your real name.</p>

          <h2>2. How We Use Your Information</h2>
          <p>We use personal data for the following purposes:</p>

          <h3>2.1. Operating the League</h3>
          <ul>
            <li>Managing accounts</li>
            <li>Running quizzes and events</li>
            <li>Tracking results and maintaining leaderboards</li>
            <li>Processing payments</li>
            <li>Communicating with players</li>
          </ul>

          <h3>2.2. Fairness, Enforcement, and Anti-Cheating</h3>
          <p>
            To ensure integrity of competition, recorded gameplay and other data
            may be used to:
          </p>
          <ul>
            <li>Verify compliance with rules</li>
            <li>Detect cheating or unfair behavior</li>
            <li>Resolve disputes</li>
            <li>Investigate suspicious activity</li>
            <li>Support bans or disciplinary actions</li>
          </ul>
          <p>
            We may use multiple anti-cheating tools, including automated or
            AI-powered systems. We do not disclose specifics of these tools.
          </p>

          <h3>2.3. Content Publication</h3>
          <p>With your participation:</p>
          <ul>
            <li>
              Gameplay recordings may be published on YouTube or other platforms
            </li>
            <li>Clips may be used for promotional or educational purposes</li>
            <li>Score data may appear in publicly accessible results</li>
          </ul>
          <p>This is covered in the Terms & Conditions.</p>

          <h3>2.4. Service Improvement</h3>
          <p>We use aggregated and anonymized data to:</p>
          <ul>
            <li>Improve quiz formats</li>
            <li>Enhance website functionality</li>
            <li>Analyse participation trends</li>
            <li>Develop future KFL features</li>
          </ul>

          <h2>3. Legal Basis for Processing (GDPR-Friendly)</h2>
          <p>We process personal data on the following bases:</p>
          <ul>
            <li>
              <strong>Contractual necessity</strong> — to provide league access
              and run quizzes
            </li>
            <li>
              <strong>Legitimate interests</strong> — maintaining fairness,
              preventing cheating, ensuring security
            </li>
            <li>
              <strong>Consent</strong> — camera use, recordings, communications
            </li>
            <li>
              <strong>Legal obligations</strong> — accounting or tax
              requirements for payments
            </li>
          </ul>

          <h2>4. How We Store and Protect Data</h2>
          <p>
            4.1. We store data securely using industry-standard protection
            measures.
          </p>
          <p>
            4.2. We do not publicly disclose details of our storage locations or
            infrastructure for security reasons.
          </p>
          <p>
            4.3. Access to personal data is restricted to authorized KFL
            administration only.
          </p>
          <p>4.4. We do not sell or rent personal data to any third parties.</p>

          <h2>5. Data Sharing</h2>
          <p>We share data only when necessary:</p>
          <ul>
            <li>
              <strong>Payment processors</strong> (PayPal, Wise, Payoneer,
              Monobank) → We receive only transaction confirmations.
            </li>
            <li>
              <strong>Service providers</strong> used for website hosting or
              email delivery
            </li>
            <li>
              <strong>Video platforms</strong> (e.g., YouTube), if your gameplay
              video is published
            </li>
            <li>
              <strong>Law enforcement</strong>, if legally required in
              exceptional cases
            </li>
          </ul>
          <p>We do not share personal data for advertising purposes.</p>

          <h2>6. Data Retention</h2>
          <ul>
            <li>
              <strong>Account information:</strong> kept as long as your account
              is active
            </li>
            <li>
              <strong>Leaderboard data:</strong> kept permanently unless removal
              is requested
            </li>
            <li>
              <strong>Emails and support messages:</strong> up to 5 years
            </li>
            <li>
              <strong>Raw recording files</strong> (if not published): deleted
              after 12 months
            </li>
            <li>
              <strong>Published videos:</strong> remain online indefinitely
            </li>
          </ul>
          <p>
            You may request deletion of your account data, except for
            information already made public (e.g., published YouTube videos or
            leaderboard appearances).
          </p>

          <h2>7. Your Rights</h2>
          <p>Depending on your jurisdiction, you may have rights to:</p>
          <ul>
            <li>Access your personal data</li>
            <li>Request correction</li>
            <li>Request deletion</li>
            <li>Request export of your data</li>
            <li>Withdraw consent (for future processing)</li>
            <li>Object to certain processing</li>
            <li>Limit processing in specific situations</li>
          </ul>
          <p>To exercise these rights, contact us at the email listed below.</p>
          <p>
            Once a recording is published, we cannot remove it from third-party
            platforms retroactively unless allowed by the platform's policies.
          </p>

          <h2>8. Children's Privacy</h2>
          <p>
            KFL is not intended for children under 16. We do not knowingly
            collect personal data from minors below the age of digital consent
            in their jurisdiction.
          </p>

          <h2>9. International Data Transfers</h2>
          <p>
            Because KFL processes payments and uses international platforms
            (e.g., PayPal, YouTube), your data may be processed outside Ukraine.
            We take reasonable steps to protect your data regardless of
            location.
          </p>

          <h2>10. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy at any time. Material changes will
            be announced via the website or email. Continued use of KFL after
            updates constitutes acceptance of the new version.
          </p>

          <h2>11. Contact Us</h2>
          <p>For any questions or data requests, contact:</p>
          <p>
            <a href="mailto:knowledge.facts.ladder@gmail.com">
              knowledge.facts.ladder@gmail.com
            </a>
            <br />
            <a href="https://www.kflquiz.com">www.kflquiz.com</a>
            <br />
            Ukraine, Kharkiv, Pavla Tychyny 23, 61009
          </p>
        </article>
      </main>
    </div>
  )
}
